// API响应处理工具

/**
 * 业务错误码定义（与后端保持一致）
 */
export const BusinessCode = {
  SUCCESS: 0,
  // 1xxx - 参数相关错误
  INVALID_PARAM: 1001,
  PARAM_VALIDATION: 1002,
  MISSING_PARAM: 1003,
  INVALID_HASH_FORMAT: 1004,
  HASH_COUNT_EXCEEDED: 1005,
  INVALID_ORDER_TYPE: 1006,
  INVALID_QUANTITY: 1007,
  FILE_TOO_LARGE: 1008,
  // 2xxx - 认证授权相关错误
  UNAUTHORIZED: 2001,
  TOKEN_INVALID: 2002,
  TOKEN_EXPIRED: 2003,
  TOKEN_MISSING: 2004,
  // 3xxx - 权限相关错误
  FORBIDDEN: 3001,
  ORDER_TYPE_MISMATCH: 3002,
  ORDER_STATUS_ERROR: 3003,
  // 4xxx - 资源不存在
  NOT_FOUND: 4001,
  ORDER_NOT_FOUND: 4002,
  FILE_NOT_FOUND: 4003,
  // 5xxx - 服务器内部错误
  INTERNAL_ERROR: 5001,
  DATABASE_ERROR: 5002,
  FILE_OPERATION_ERROR: 5003,
  ORDER_CREATE_FAILED: 5004,
  ORDER_UPDATE_FAILED: 5005,
  // 6xxx - 限流相关错误
  TOO_MANY_REQUESTS: 6001,
};

/**
 * 检查响应是否成功
 * @param {Object} response - axios响应对象
 * @returns {boolean} - 是否成功
 */
export function isSuccessResponse(response) {
  return response && response.data && response.data.code === BusinessCode.SUCCESS;
}

/**
 * 从响应中提取数据
 * @param {Object} response - axios响应对象
 * @returns {*} - 响应数据
 */
export function extractData(response) {
  if (!response || !response.data) {
    return null;
  }
  return response.data.data;
}

/**
 * 从响应中提取消息
 * @param {Object} response - axios响应对象
 * @returns {string} - 响应消息
 */
export function extractMessage(response) {
  if (!response || !response.data) {
    return '';
  }
  return response.data.message || '';
}

/**
 * 从响应中提取业务错误码
 * @param {Object} response - axios响应对象
 * @returns {number} - 业务错误码
 */
export function extractBusinessCode(response) {
  if (!response || !response.data) {
    return BusinessCode.INTERNAL_ERROR;
  }
  return response.data.code || BusinessCode.INTERNAL_ERROR;
}

/**
 * 处理API错误响应
 * @param {Error} error - axios错误对象
 * @returns {Object} - { code, message, httpStatus }
 */
export function handleApiError(error) {
  if (!error.response) {
    // 网络错误或其他错误
    return {
      code: BusinessCode.INTERNAL_ERROR,
      message: '网络错误，请检查网络连接',
      httpStatus: 0,
    };
  }

  const response = error.response;
  const businessCode = extractBusinessCode(response);
  const message = extractMessage(response) || response.statusText || '请求失败';

  return {
    code: businessCode,
    message: message,
    httpStatus: response.status,
  };
}

/**
 * 根据业务错误码获取用户友好的错误消息
 * @param {number} code - 业务错误码
 * @returns {string} - 错误消息
 */
export function getErrorMessage(code) {
  const messages = {
    [BusinessCode.SUCCESS]: '操作成功',
    [BusinessCode.INVALID_PARAM]: '参数错误',
    [BusinessCode.PARAM_VALIDATION]: '参数验证失败',
    [BusinessCode.MISSING_PARAM]: '缺少必要参数',
    [BusinessCode.INVALID_HASH_FORMAT]: 'Hash格式无效',
    [BusinessCode.HASH_COUNT_EXCEEDED]: 'Hash数量超过限制',
    [BusinessCode.INVALID_ORDER_TYPE]: '订单类型无效',
    [BusinessCode.INVALID_QUANTITY]: '数量无效',
    [BusinessCode.FILE_TOO_LARGE]: '文件过大',
    [BusinessCode.UNAUTHORIZED]: '未授权',
    [BusinessCode.TOKEN_INVALID]: '凭据无效',
    [BusinessCode.TOKEN_EXPIRED]: '凭据已过期',
    [BusinessCode.TOKEN_MISSING]: '缺少凭据',
    [BusinessCode.FORBIDDEN]: '权限不足',
    [BusinessCode.ORDER_TYPE_MISMATCH]: '订单类型不匹配',
    [BusinessCode.ORDER_STATUS_ERROR]: '订单状态异常',
    [BusinessCode.NOT_FOUND]: '资源不存在',
    [BusinessCode.ORDER_NOT_FOUND]: '订单不存在',
    [BusinessCode.FILE_NOT_FOUND]: '文件不存在',
    [BusinessCode.INTERNAL_ERROR]: '服务器内部错误',
    [BusinessCode.DATABASE_ERROR]: '数据库错误',
    [BusinessCode.FILE_OPERATION_ERROR]: '文件操作失败',
    [BusinessCode.ORDER_CREATE_FAILED]: '订单创建失败',
    [BusinessCode.ORDER_UPDATE_FAILED]: '订单更新失败',
    [BusinessCode.TOO_MANY_REQUESTS]: '请求过于频繁，请稍后重试',
  };
  return messages[code] || '未知错误';
}
